/**
 * HTTP Polling Transport Implementation
 *
 * Establishes connection via polling HTTP requests.
 * Fallback for environments where WebSocket is unavailable.
 * Handles backpressure and adaptive polling intervals.
 */

import { BaseTransport } from './BaseTransport';
import type { TransportConfig } from '../types';
import { calculateRetryDelay, delay } from '../utils';

export class HttpPollingTransport extends BaseTransport {
  private abortController: AbortController | null = null;

  private pollingInterval = 1000; // Start with 1 second

  private minPollingInterval = 500;

  private maxPollingInterval = 30000;

  private lastPollTime = 0;

  private isPolling = false;

  constructor(id: string, config: TransportConfig) {
    super(id, 'http-polling', config);
    this.pollingInterval = config.reconnect?.initialDelayMs || 1000;
    this.minPollingInterval = config.reconnect?.initialDelayMs || 500;
    this.maxPollingInterval = config.reconnect?.maxDelayMs || 30000;
  }

  async connect(): Promise<void> {
    if (this.isPolling) {
      return;
    }

    this.setStatus('connecting');
    this.abortController = new AbortController();
    this.isPolling = true;

    try {
      // Start polling loop
      this.poll();
      this.setStatus('connected');
    } catch (error) {
      this.setStatus('error');
      this.emitError(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    this.isPolling = false;
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.setStatus('disconnected');
    this.cleanup();
  }

  async send(data: string | ArrayBuffer): Promise<void> {
    if (!this.isPolling) {
      throw new Error('HTTP polling connection not active');
    }

    try {
      const body = typeof data === 'string' ? data : new TextDecoder().decode(new Uint8Array(data));

      const response = await fetch(this.config.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.config.headers,
        },
        body,
        signal: this.abortController?.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        this.emitError(error);
        throw error;
      }
    }
  }

  private async poll(): Promise<void> {
    let consecutiveErrors = 0;

    while (this.isPolling) {
      try {
        const now = Date.now();
        const timeSinceLastPoll = now - this.lastPollTime;

        if (timeSinceLastPoll < this.pollingInterval) {
          await delay(this.pollingInterval - timeSinceLastPoll);
        }

        this.lastPollTime = Date.now();

        const response = await fetch(this.config.url, {
          method: 'GET',
          headers: this.config.headers,
          signal: this.abortController?.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const text = await response.text();
        if (text) {
          this.emitMessage(text);
        }

        // Reduce polling interval on success
        if (consecutiveErrors > 0) {
          this.pollingInterval = Math.max(this.minPollingInterval, this.pollingInterval / 2);
          consecutiveErrors = 0;
          if (this.status === 'reconnecting') {
            this.setStatus('connected');
          }
        }
      } catch (error) {
        consecutiveErrors++;

        if (error instanceof Error && error.name === 'AbortError') {
          break;
        }

        // Exponential backoff on error
        const delayMs = calculateRetryDelay(consecutiveErrors, {
          initialDelayMs: this.minPollingInterval,
          maxDelayMs: this.maxPollingInterval,
          backoffMultiplier: 2,
          maxAttempts: 0,
        });

        if (delayMs) {
          this.pollingInterval = delayMs;
          if (consecutiveErrors === 1) {
            this.setStatus('reconnecting');
          }
        }

        if (error instanceof Error) {
          this.logger.error(`HTTP polling error (attempt ${consecutiveErrors}):`, error);
        }
      }
    }
  }
}
